import { useContext, useState, useRef, useEffect } from "react";
import { appContext } from "../../App";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";

const Switcher: React.FC = () => {
  const {
    authUserName,
    setAuthUserName,
    authUserEmail,
    setAuthUserEmail,
    authList,
  } = useContext(appContext);

  const { setActiveAccount } = useAccount();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  function handleSelect(email: string) {
    const user = authList.find((n: any) => n.email === email);
    const userId = user?.userId || user?.id || email;
    setAuthUserEmail(email);
    if (user) setAuthUserName(user.userName);
    setActiveAccount(userId);
    axios.defaults.headers.common["userId"] = userId;

    setOpen(false);
    setSearch("");
  }

  function normalizeBithash(param: string): string {
    return param?.replace(/^bithash_.*/, 'bithash');
  }

  function cropBithashPrefix(param: string): string {
    return param?.replace(/^bithash_/, '');
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!authList.length) return null;

  const filteredList = authList
    .slice()
    .sort((a: any, b: any) => a.email?.localeCompare(b.email))
    .filter(
      (n: any) =>
        n.email?.toLowerCase().includes(search.toLowerCase()) ||
        n.userName?.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div className="flex justify-end">
      <div
        ref={containerRef}
        className="relative"
      >
        <div className="w-56">
          {/* Selected value */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-full bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-left flex justify-between items-center text-xs md:text-sm shadow-sm hover:shadow-md transition-all font-bold"
          >
            <span className="truncate">
              {authUserEmail
                ? cropBithashPrefix(authUserEmail)
                : "Select user"}
            </span>
            <span className={`ml-2 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
          </button>

          {/* Dropdown */}
          {open && (
            <div className="absolute z-50 mt-2 w-full bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              {/* Search */}
              <input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 border-b border-slate-100 dark:border-slate-700 outline-none text-sm placeholder-slate-400 focus:bg-white dark:focus:bg-slate-800 transition-colors"
                autoFocus
              />

              {/* Options */}
              <div className="max-h-48 overflow-y-auto">
                {filteredList.length === 0 && (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    No users found
                  </div>
                )}

                {filteredList.map((n: any) => (
                  <div
                    key={n.email}
                    onClick={() => handleSelect(n.email)}
                    className={`px-4 py-3 cursor-pointer text-sm transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20 ${authUserEmail === n.email ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                  >
                    <div className="font-bold text-slate-800 dark:text-slate-100">
                      {normalizeBithash(n.userName)}
                    </div>
                    <div className="text-slate-500 dark:text-slate-400 text-xs truncate mt-0.5 opacity-70">
                      {cropBithashPrefix(n.email)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Switcher;